import React, { useState, useRef } from 'react';
import { api } from '../services/api';
import { LANGUAGES, setLang, t as translate } from '../i18n';

const LOCATIONS = ['Indoor','Outdoor','Bar','Terrace','First Floor','Second Floor','Private','Rooftop','Patio'];
const DAYS = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];

const defaultHours = {
  monday:    { open: '11:00', close: '22:00', closed: false },
  tuesday:   { open: '11:00', close: '22:00', closed: false },
  wednesday: { open: '11:00', close: '22:00', closed: false },
  thursday:  { open: '11:00', close: '22:00', closed: false },
  friday:    { open: '11:00', close: '23:00', closed: false },
  saturday:  { open: '10:00', close: '23:00', closed: false },
  sunday:    { open: '10:00', close: '21:00', closed: false },
};

// Steps: 0=lang 1=welcome 2=tables 3=hours 4=menu 5=tablock 6=done
const TOTAL_STEPS = 7;

const LOCKABLE_TABS = [
  { key: 'list', label: 'Reservations' },
  { key: 'tables',       label: 'Tables' },
  { key: 'menu',         label: 'Menu' },
  { key: 'analytics',    label: 'Analytics' },
  { key: 'waitlist',     label: 'Waitlist' },
  { key: 'settings',     label: 'Settings' },
];

function compressImage(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const MAX = 1200;
        let { width, height } = img;
        if (width > MAX || height > MAX) {
          if (width > height) { height = Math.round(height * MAX / width); width = MAX; }
          else { width = Math.round(width * MAX / height); height = MAX; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.82).split(',')[1]);
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  });
}

// Inject spinner keyframe once
if (!document.getElementById('seva-onb-spin')) {
  const st = document.createElement('style');
  st.id = 'seva-onb-spin';
  st.textContent = '@keyframes spin{to{transform:rotate(360deg)}}';
  document.head.appendChild(st);
}

export default function Onboarding({ onComplete }) {
  const [lang, setLangState]  = useState('en');
  const [step, setStep]       = useState(0);
  const [tables, setTables]   = useState([{ label: '', capacity: 2, location: 'Indoor' }]);
  const [hours, setHours]     = useState(defaultHours);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');

  // Menu step state
  const [menuScan, setMenuScan]       = useState(null); // 'scanning' | 'review' | null
  const [menuItems, setMenuItems]     = useState([]);
  const [menuSaving, setMenuSaving]   = useState(false);
  const fileRef = useRef(null);

  // Tab lock step
  const [lockTabs, setLockTabs]   = useState([]);
  const [lockPwd, setLockPwd]     = useState('');
  const [lockPwd2, setLockPwd2]   = useState('');
  const [lockErr, setLockErr]     = useState('');
  const [lockSaving, setLockSaving] = useState(false);

  const T = (key) => translate(key, lang);
  const rtl = !!(LANGUAGES.find(x => x.code === lang) || {}).rtl;

  const selectLang = (code) => { setLangState(code); setLang(code); };

  const addRow    = () => setTables(t => [...t, { label: '', capacity: 2, location: 'Indoor' }]);
  const removeRow = i  => setTables(t => t.filter((_, idx) => idx !== i));
  const setRow    = (i, field, val) => setTables(t => t.map((r, idx) => idx === i ? { ...r, [field]: val } : r));
  const setH      = (day, field, val) => setHours(h => ({ ...h, [day]: { ...h[day], [field]: val } }));

  const saveTables = async () => {
    const valid = tables.filter(t => t.label.trim() && t.capacity > 0);
    if (!valid.length) { setError('Add at least one table.'); return; }
    setError(''); setSaving(true);
    try {
      for (const t of valid) {
        await api.createTable({ label: t.label.trim(), capacity: parseInt(t.capacity), location: t.location.toLowerCase() });
      }
      setStep(3);
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  };

  const saveHours = async () => {
    setSaving(true); setError('');
    try {
      const langLabel = (LANGUAGES.find(l => l.code === lang) || {}).label || 'English';
      await api.updateSettings({ opening_hours: hours, ui_language: lang, primary_language: langLabel });
      setStep(4);
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  };

  const handleMenuFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setMenuScan('scanning');
    setError('');
    try {
      const b64 = await compressImage(file);
      const { items } = await api.scanMenu(b64);
      setMenuItems((items || []).map(it => ({ ...it, include: true })));
      setMenuScan('review');
    } catch (err) {
      setError('Could not scan menu. You can add it later from the Menu tab.');
      setMenuScan(null);
    }
  };

  const saveMenu = async () => {
    const toSave = menuItems.filter(it => it.include && it.name);
    setMenuSaving(true);
    try {
      if (toSave.length > 0) await api.bulkCreateMenu(toSave);
      setStep(5); // go to tab lock step
    } catch (err) { setError(err.message); } finally { setMenuSaving(false); }
  };

  const saveTabLock = async () => {
    if (lockTabs.length === 0) { setStep(6); return; }
    if (!lockPwd || lockPwd.length < 4) { setLockErr('Password must be at least 4 characters'); return; }
    if (lockPwd !== lockPwd2) { setLockErr('Passwords do not match'); return; }
    setLockErr(''); setLockSaving(true);
    try {
      await api.setOwnerPassword('', lockPwd);
      await api.updateSettings({ locked_tabs: lockTabs });
      setStep(6);
    } catch (e) { setLockErr(e.message); } finally { setLockSaving(false); }
  };

  return (
    <div style={s.overlay}>
      <div style={{ ...s.modal, direction: rtl ? 'rtl' : 'ltr' }}>

        {/* Progress dots */}
        <div style={s.dots}>
          {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
            <div key={i} style={{ ...s.dot, ...(i <= step ? s.dotActive : {}) }} />
          ))}
        </div>

        {/* ── Step 0: Language ── */}
        {step === 0 && (
          <div style={s.body}>
            <div style={s.iconWrap}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/>
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
              </svg>
            </div>
            <h1 style={s.h1}>{T('onb_lang_title')}</h1>
            <p style={s.p}>{T('onb_lang_sub')}</p>
            <div style={s.langGrid}>
              {LANGUAGES.map(l => (
                <button key={l.code} style={{ ...s.langBtn, ...(lang === l.code ? s.langBtnActive : {}) }} onClick={() => selectLang(l.code)}>
                  {l.label}
                </button>
              ))}
            </div>
            <button style={s.btn} onClick={() => setStep(1)}>{T('onb_lets_go')}</button>
          </div>
        )}

        {/* ── Step 1: Welcome ── */}
        {step === 1 && (
          <div style={s.body}>
            <div style={s.iconWrap}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.9)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 2h18M3 22h18M12 2v20M3 12h18" />
              </svg>
            </div>
            <h1 style={s.h1}>{T('onb_welcome')}</h1>
            <p style={s.p}>{T('onb_welcome_sub')}</p>
            <div style={s.steps}>
              <div style={s.stepItem}><div style={s.stepNum}>1</div><span>{T('onb_step_tables')}</span></div>
              <div style={s.stepItem}><div style={s.stepNum}>2</div><span>{T('onb_step_hours')}</span></div>
              <div style={s.stepItem}><div style={s.stepNum}>3</div><span>Add your menu (optional)</span></div>
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <button style={s.back} onClick={() => setStep(0)}>{T('back')}</button>
              <button style={s.btn} onClick={() => setStep(2)}>{T('onb_lets_go')}</button>
              <button style={s.skip} onClick={() => setStep(6)}>Skip all</button>
            </div>
          </div>
        )}

        {/* ── Step 2: Tables ── */}
        {step === 2 && (
          <div style={s.body}>
            <h2 style={s.h2}>{T('onb_tables_title')}</h2>
            <p style={s.p}>{T('onb_tables_sub')}</p>
            {error && <div style={s.err}>{error}</div>}
            <div style={s.tableList}>
              <div style={s.tableHeader}>
                <span style={{ flex: 1.2 }}>{T('onb_col_name')}</span>
                <span style={{ flex: 0.7 }}>{T('onb_col_seats')}</span>
                <span style={{ flex: 1.5 }}>{T('onb_col_area')}</span>
                <span style={{ width: 28 }} />
              </div>
              {tables.map((row, i) => (
                <div key={i} style={s.tableRow}>
                  <input style={{ ...s.input, flex: 1.2 }} placeholder="e.g. T1" value={row.label} onChange={e => setRow(i, 'label', e.target.value)} />
                  <input style={{ ...s.input, flex: 0.7 }} type="number" min="1" max="30" value={row.capacity} onChange={e => setRow(i, 'capacity', e.target.value)} />
                  <select style={{ ...s.input, flex: 1.5 }} value={row.location} onChange={e => setRow(i, 'location', e.target.value)}>
                    {LOCATIONS.map(l => <option key={l} value={l}>{l}</option>)}
                  </select>
                  {tables.length > 1 && <button style={s.removeBtn} onClick={() => removeRow(i)}>×</button>}
                </div>
              ))}
              <button style={s.addRowBtn} onClick={addRow}>{T('onb_add_table')}</button>
            </div>
            <div style={s.actions}>
              <button style={s.back} onClick={() => setStep(1)}>{T('back')}</button>
              <button style={s.btn} onClick={saveTables} disabled={saving}>{saving ? T('saving') : `${T('next')} →`}</button>
            </div>
          </div>
        )}

        {/* ── Step 3: Hours ── */}
        {step === 3 && (
          <div style={s.body}>
            <h2 style={s.h2}>{T('onb_hours_title')}</h2>
            <p style={s.p}>{T('onb_hours_sub')}</p>
            {error && <div style={s.err}>{error}</div>}
            <div style={s.hoursGrid}>
              {DAYS.map(day => {
                const h = hours[day];
                return (
                  <div key={day} style={s.dayRow}>
                    <span style={s.dayName}>{T(day)}</span>
                    <label style={s.closedLabel}>
                      <input type="checkbox" checked={!!h.closed} onChange={e => setH(day, 'closed', e.target.checked)} style={{ marginRight: 5, accentColor: '#4f46e5' }} />
                      {T('closed')}
                    </label>
                    {!h.closed && (
                      <>
                        <input style={s.timeIn} type="time" value={h.open}  onChange={e => setH(day, 'open',  e.target.value)} />
                        <span style={s.to}>→</span>
                        <input style={s.timeIn} type="time" value={h.close} onChange={e => setH(day, 'close', e.target.value)} />
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            <div style={s.actions}>
              <button style={s.back} onClick={() => setStep(2)}>{T('back')}</button>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <button style={s.skip} onClick={() => setStep(4)}>Skip</button>
                <button style={s.btn} onClick={saveHours} disabled={saving}>{saving ? T('saving') : `${T('next')} →`}</button>
              </div>
            </div>
          </div>
        )}

        {/* ── Step 4: Menu ── */}
        {step === 4 && (
          <div style={s.body}>
            <div style={s.iconWrap}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 6h18M3 12h18M3 18h18"/>
              </svg>
            </div>
            <h2 style={s.h2}>Add your menu</h2>
            <p style={s.p}>Take a photo of your physical menu or upload an image — the AI will extract all items automatically. Your staff AI can then answer customer questions about ingredients, allergens, and dishes.</p>
            {error && <div style={s.err}>{error}</div>}

            {menuScan === 'scanning' && (
              <div style={s.scanningBox}>
                <div style={s.spinner} />
                <span style={{ fontSize: 14, color: '#64748b' }}>Scanning your menu…</span>
              </div>
            )}

            {menuScan === 'review' && menuItems.length > 0 && (
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', marginBottom: 8 }}>Found {menuItems.length} items. Uncheck any you don't want to save:</div>
                <div style={{ maxHeight: 220, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
                  {menuItems.map((item, i) => (
                    <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: item.include ? '#f0f4ff' : '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0', cursor: 'pointer' }}>
                      <input type="checkbox" checked={!!item.include} onChange={e => setMenuItems(prev => prev.map((it, idx) => idx === i ? { ...it, include: e.target.checked } : it))} style={{ accentColor: '#4f46e5' }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{item.name}</div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>{item.category}{item.price ? ` · ${item.price}` : ''}</div>
                      </div>
                    </label>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button style={s.btn} onClick={saveMenu} disabled={menuSaving}>{menuSaving ? 'Saving…' : `Save ${menuItems.filter(it => it.include).length} items →`}</button>
                  <button style={s.skip} onClick={() => setStep(5)}>Skip</button>
                </div>
              </div>
            )}

            {!menuScan && (
              <>
                <input ref={fileRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={handleMenuFile} />
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <button style={s.btn} onClick={() => fileRef.current?.click()}>
                    📷 Scan menu photo
                  </button>
                  <button style={s.back} onClick={() => setStep(3)}>Back</button>
                  <button style={s.skip} onClick={() => setStep(5)}>Skip — I'll do this later</button>  {/* step 5 = tab lock */}
                </div>
              </>
            )}
          </div>
        )}

        {/* ── Step 5: Tab lock ── */}
        {step === 5 && (
          <div style={s.body}>
            <div style={s.iconWrap}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
            </div>
            <h2 style={s.h2}>Tab lock <span style={{ fontSize: 13, fontWeight: 500, color: '#94a3b8', letterSpacing: 0 }}>optional</span></h2>
            <p style={s.p}>Restrict access to sensitive tabs with an owner password. Staff can use everything else freely. You can change this any time in Settings.</p>
            {lockErr && <div style={s.err}>{lockErr}</div>}

            <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Select tabs to lock</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {LOCKABLE_TABS.map(({ key, label }) => {
                const sel = lockTabs.includes(key);
                return (
                  <button key={key} type="button"
                    onClick={() => setLockTabs(prev => sel ? prev.filter(k => k !== key) : [...prev, key])}
                    style={{ padding: '8px 16px', borderRadius: 9, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                      border: sel ? '1.5px solid #4f46e5' : '1.5px solid #e2e8f0',
                      background: sel ? '#eef2ff' : '#f8fafc',
                      color: sel ? '#4f46e5' : '#64748b' }}
                  >
                    {sel ? '🔒 ' : ''}{label}
                  </button>
                );
              })}
            </div>

            {lockTabs.length > 0 && (
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 180, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Owner password</label>
                  <input style={s.input} type="password" placeholder="Min. 4 characters" value={lockPwd} onChange={e => { setLockPwd(e.target.value); setLockErr(''); }} />
                </div>
                <div style={{ flex: 1, minWidth: 180, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Confirm password</label>
                  <input style={s.input} type="password" value={lockPwd2} onChange={e => { setLockPwd2(e.target.value); setLockErr(''); }} />
                </div>
              </div>
            )}

            <div style={s.actions}>
              <button style={s.back} onClick={() => setStep(4)}>Back</button>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <button style={s.skip} onClick={() => setStep(6)}>Skip</button>
                <button style={s.btn} onClick={saveTabLock} disabled={lockSaving}>
                  {lockSaving ? 'Saving…' : lockTabs.length > 0 ? 'Save & continue →' : 'Continue →'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Step 6: Done ── */}
        {step === 6 && (
          <div style={{ ...s.body, alignItems: 'center', textAlign: 'center' }}>
            <div style={{ ...s.iconWrap, background: '#059669' }}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h2 style={s.h2}>{T('onb_done_title')}</h2>
            <p style={s.p}>{T('onb_done_sub')}</p>
            <button style={s.btn} onClick={onComplete}>{T('onb_go_dashboard')}</button>
          </div>
        )}

      </div>
    </div>
  );
}

const s = {
  overlay:  { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 },
  modal:    { background: '#fff', borderRadius: 24, width: '100%', maxWidth: 600, boxShadow: '0 24px 80px rgba(15,23,42,0.25)', overflow: 'hidden', maxHeight: '90vh', overflowY: 'auto' },
  dots:     { display: 'flex', gap: 6, padding: '20px 28px 0', justifyContent: 'center' },
  dot:      { width: 8, height: 8, borderRadius: '50%', background: '#e2e8f0', transition: 'background 0.2s' },
  dotActive:{ background: '#4f46e5' },
  body:     { padding: '28px 32px 32px', display: 'flex', flexDirection: 'column', gap: 16 },
  iconWrap: { width: 52, height: 52, background: '#4f46e5', borderRadius: 15, display: 'flex', alignItems: 'center', justifyContent: 'center', alignSelf: 'flex-start' },
  h1:       { fontSize: 26, fontWeight: 800, color: '#0f172a', margin: 0, letterSpacing: '-0.4px' },
  h2:       { fontSize: 22, fontWeight: 800, color: '#0f172a', margin: 0, letterSpacing: '-0.3px' },
  p:        { fontSize: 14, color: '#64748b', margin: 0, lineHeight: 1.6 },

  langGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 },
  langBtn:  { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 14px', fontSize: 14, color: '#475569', cursor: 'pointer', textAlign: 'center', fontWeight: 500, transition: 'all 0.15s' },
  langBtnActive: { background: '#4f46e5', borderColor: '#4f46e5', color: '#fff', fontWeight: 700 },

  steps:    { display: 'flex', flexDirection: 'column', gap: 10, background: '#f8fafc', borderRadius: 14, padding: '16px 20px' },
  stepItem: { display: 'flex', alignItems: 'center', gap: 12, fontSize: 14, color: '#334155', fontWeight: 500 },
  stepNum:  { width: 26, height: 26, borderRadius: '50%', background: '#4f46e5', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 },

  btn:      { background: '#4f46e5', border: 'none', borderRadius: 12, padding: '13px 28px', fontSize: 15, fontWeight: 700, color: '#fff', cursor: 'pointer', alignSelf: 'flex-start' },
  back:     { background: 'none', border: '1px solid #e2e8f0', borderRadius: 12, padding: '13px 22px', fontSize: 14, color: '#64748b', cursor: 'pointer' },
  skip:     { background: 'none', border: 'none', fontSize: 13, color: '#94a3b8', cursor: 'pointer', padding: '4px 8px', textDecoration: 'underline' },
  actions:  { display: 'flex', gap: 10, justifyContent: 'space-between', marginTop: 4, alignItems: 'center' },
  err:      { background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 10, padding: '10px 14px', fontSize: 13 },

  tableList:   { display: 'flex', flexDirection: 'column', gap: 8 },
  tableHeader: { display: 'flex', gap: 8, fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.07em', padding: '0 4px' },
  tableRow:    { display: 'flex', gap: 8, alignItems: 'center' },
  input:       { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 9, padding: '9px 12px', fontSize: 13, color: '#0f172a', outline: 'none', minWidth: 0 },
  removeBtn:   { width: 28, height: 28, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 7, color: '#dc2626', cursor: 'pointer', fontSize: 16, flexShrink: 0 },
  addRowBtn:   { background: 'none', border: '1px dashed #cbd5e1', borderRadius: 9, padding: '9px 16px', fontSize: 13, color: '#64748b', cursor: 'pointer', textAlign: 'left' },

  hoursGrid:   { display: 'flex', flexDirection: 'column', gap: 8 },
  dayRow:      { display: 'flex', alignItems: 'center', gap: 10 },
  dayName:     { width: 100, fontSize: 13, fontWeight: 600, color: '#334155', flexShrink: 0 },
  closedLabel: { display: 'flex', alignItems: 'center', fontSize: 13, color: '#94a3b8', width: 76, flexShrink: 0, cursor: 'pointer' },
  timeIn:      { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '7px 10px', fontSize: 13, color: '#0f172a', outline: 'none' },
  to:          { fontSize: 12, color: '#94a3b8' },

  scanningBox: { display: 'flex', alignItems: 'center', gap: 12, padding: '20px', background: '#f8fafc', borderRadius: 12, border: '1px solid #e2e8f0' },
  spinner:     { width: 20, height: 20, border: '2px solid #e2e8f0', borderTopColor: '#4f46e5', borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 },
};
